import { useRef, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ReCAPTCHA from "react-google-recaptcha";
import { ArrowLeft } from "lucide-react";
import { RequestMagicLinkInputSchema } from "@satyrsmc/shared/dto/auth";
import type { RequestMagicLinkInput } from "@satyrsmc/shared/dto/auth";
import { trpc } from "@/trpc";
import { useAuth } from "@/state/AuthContext";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PageLoading } from "@/components/layout/PageLoading";
import { ENV } from "@/env";

const RECAPTCHA_SITE_KEY = ENV.RECAPTCHA_SITE_KEY;

export function LoginPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect") ?? "/";
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [checkEmail, setCheckEmail] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [pendingReview, setPendingReview] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirect, { replace: true });
    }
  }, [isAuthenticated, redirect, navigate]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<RequestMagicLinkInput>({
    resolver: zodResolver(RequestMagicLinkInputSchema),
    defaultValues: {
      email: "",
      redirect,
      recaptcha_token: RECAPTCHA_SITE_KEY ? "" : "disabled",
    },
  });

  const requestMagicLinkMutation = trpc.auth.requestMagicLink.useMutation({
    onSuccess: (data) => {
      if (data.suspended) {
        setSuspended(true);
      } else if (data.pending_review) {
        setPendingReview(true);
      } else if (data.not_found) {
        setNotFound(true);
      } else {
        setCheckEmail(true);
      }
    },
    onError: () => {
      if (RECAPTCHA_SITE_KEY) {
        recaptchaRef.current?.reset();
        setValue("recaptcha_token", "");
      }
    },
  });

  if (isLoading) return <PageLoading />;

  if (isAuthenticated) return null;

  if (pendingReview) {
    return (
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Registration Under Review</CardTitle>
          <CardDescription>
            Your account is currently under review. You'll receive an email once it's approved.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (suspended) {
    return (
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Account Rejected</CardTitle>
          <CardDescription>
            Your registration was not approved. Contact the club for more information.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" className="w-full" onClick={() => setSuspended(false)}>
            Try a different email
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (checkEmail || notFound) {
    return (
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Check Your Email</CardTitle>
          <CardDescription>
            If that email is on file, a login link has been sent. Check your inbox.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" className="w-full" onClick={() => setCheckEmail(false)}>
            Try a different email
          </Button>
        </CardContent>
      </Card>
    );
  }

  const onSubmit = (data: RequestMagicLinkInput) => {
    requestMagicLinkMutation.mutate(data);
  };

  const handleRecaptchaChange = (token: string | null) => {
    setValue("recaptcha_token", token ?? "", { shouldValidate: true });
  };

  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <CardTitle>Log In</CardTitle>
        <CardDescription>
          You've reached the Satyrs M/C members' site. To continue, please enter your email to
          receive a login link.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {requestMagicLinkMutation.error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              Something went wrong. Please try again.
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" {...register("email")} />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          {RECAPTCHA_SITE_KEY && (
            <div>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                theme="dark"
                onChange={handleRecaptchaChange}
                onExpired={() => setValue("recaptcha_token", "")}
              />
              {errors.recaptcha_token && (
                <p className="text-sm text-destructive">{errors.recaptcha_token.message}</p>
              )}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || requestMagicLinkMutation.isPending}
          >
            {isSubmitting || requestMagicLinkMutation.isPending ? "Sending..." : "Send Login Link"}
          </Button>
        </form>
        <a
          href={ENV.PUBLIC_URL || "https://satyrsmc.org"}
          className="mt-2 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} />
          Back to public site
        </a>
      </CardContent>
    </Card>
  );
}
